// /maestro: the flagship gets its own stage. The page argues the thesis before
// it shows a single screen: AI broke the assumptions of human-only management,
// so delivery needs a new operating system. Screens appear only as answers to
// management questions, per the founder's positioning brief (Sep 2026).
// Vocabulary rules: no command-deck analogy, no AI-SaaS verbs, no pricing
// claims, nothing unshipped (triage agent, escalation tables stay off-page).

const maestroContent = {
  'en-US': {
    seo: {
      title: 'Maestro | The operating system for software delivery in the AI era',
      description:
        'AI changed how software gets built. Maestro changes how it gets managed: humans and AI agents delivering in one system, with visibility, control and predictability computed from the work itself.',
    },
    hero: {
      kicker: 'Maestro',
      headline: 'Software delivery needs a new operating system.',
      body: 'AI changed how software gets built. It has to change how software gets managed. Maestro brings the work, the decisions, the flow, the metrics, the humans and the AI agents into one system, so you can see what is happening, control what happens next, and know what you can realistically deliver.',
      primary: 'See how Maestro works',
      primaryHref: '#questions',
    },
    heroSvg: {
      aria: 'Humans and AI agents deliver through Maestro, which enforces the policies and renders the truth of the operation.',
      workers: ['Product lead', 'Engineer', 'AI agent', 'AI agent'],
      workersLabel: 'THE WORKERS',
      center: 'Maestro',
      centerSub: 'THE OPERATING SYSTEM',
      gates: ['One queue in', 'Limits on work in progress', 'Evidence required at done'],
      rightLabel: 'THE TRUTH, RENDERED',
      chip: 'ON TRACK',
      chipSub: 'computed, with probabilities',
      doneLabel: '23 done',
      fanBest: 'best Sep 7',
      fanLikely: 'likely Sep 14',
      fanWorst: 'worst Sep 21',
      axis: 'burn-up · forecast · record',
    },
    pattern: {
      kicker: 'A pattern we have seen for decades',
      title: 'Busy is not the same as flowing.',
      body: 'The team is working hard. And still:',
      symptoms: ['Customers are not happy', 'Dates keep slipping', 'Priorities keep changing', 'Capacity is a guess', 'Too many projects at once', 'The bug list keeps growing', 'Meetings multiply and decide little', 'The best people start to leave'],
      close: 'Those look like separate problems. They rarely are. They are symptoms of one delivery system that does not flow, and we were diagnosing this pattern long before AI existed.',
    },
    problem: {
      kicker: 'Then AI raised the stakes',
      title: 'Agents joined the team. The management system did not notice.',
      body: 'Your engineers already deliver alongside AI agents. The tools that manage delivery still assume every unit of work comes from a person: estimates, sprint plans, status meetings, boards somebody updates by hand. That gap is what Maestro closes: one management system where humans and agents work together, and the operation is run as one.',
      shifts: [
        {
          name: 'Capacity broke loose from headcount',
          detail: 'One engineer now directs several agents working concurrently. Counting people stopped predicting what the operation can deliver.',
        },
        {
          name: 'Cycle times collapsed',
          detail: 'Work that took days sometimes lands in hours. A weekly status report is stale before the meeting it was written for.',
        },
        {
          name: 'The expensive part moved',
          detail: 'Producing code got cheap. Deciding what to build, in what order, and verifying what comes back is where delivery is won now.',
        },
      ],
    },
    questions: {
      kicker: 'The management problem',
      title: 'A leader should open one screen and know the truth of the operation.',
      body: 'Without asking ten people, assembling a spreadsheet, or calling another status meeting. Maestro exists to answer six questions continuously.',
      items: [
        {
          q: 'What is happening right now?',
          a: 'Active work, queues, blockers and what just finished render live from the record of the work. Nobody compiles a status report.',
        },
        {
          q: 'What should happen next?',
          a: 'The queue orders itself by business value, risk, and how long work of that size really takes. When a human overrules it, the decision carries a name and a reason.',
        },
        {
          q: 'Are we working on the right things?',
          a: 'Every card carries its why and belongs to a release with a date and an intent. Busy is visible, and so is busy on the wrong thing.',
        },
        {
          q: 'How is the system performing?',
          a: 'Throughput, cycle time, work in progress, aging and rework, measured on work items and agent actions, never on people.',
        },
        {
          q: 'When can we actually deliver?',
          a: 'Forecasts computed from your own history, with probabilities attached, instead of a date somebody typed into a plan three months ago.',
        },
        {
          q: 'Where does a human need to step in?',
          a: 'Decisions wait on named people, impediments say who they are waiting on, and irreversible calls are reserved for humans by written policy.',
        },
      ],
    },
    pillars: {
      kicker: 'What you are buying',
      title: 'Visibility. Control. Predictability.',
      items: [
        {
          name: 'Visibility',
          detail: 'The actual state of the operation, drawn from the record of events rather than from what people remember to report.',
        },
        {
          name: 'Control',
          detail: 'Written policies govern how work enters and moves: intake through one queue, limits on work in progress, evidence required at done. You govern the system instead of chasing every worker.',
        },
        {
          name: 'Predictability',
          detail: 'Delivery dates come from observed performance, with probabilities attached, so commitments become business decisions instead of rituals of estimation.',
        },
      ],
      beneath: 'Powered by orchestration: humans and AI agents working as one operation, with agents held to evidence and people trusted with judgment.',
    },
    model: {
      kicker: 'The operating model',
      title: 'Thirty years of delivery practice, encoded into software.',
      body: 'Maestro did not start from a feature list. It started from the Lean Pyramid, the operating model refined over a decade of fixing how software organizations deliver: strategy sets direction, management runs the flow, engineering ships verified value. Maestro runs that model inside the work, so the method arrives with the system instead of sitting in a slide deck.',
      layers: ['Strategy', 'Management', 'Engineering'],
      layersOutro: 'Humans and AI agents are the workers. Maestro orchestrates the system they work in.',
      cta: 'Read the Lean Pyramid canon',
      ctaHref: '/insights',
      svg: {
        aria: 'Business strategy descends through the Lean Pyramid into Maestro, which orchestrates humans and AI workers; observed performance feeds back into the operating system.',
        strategy: 'BUSINESS STRATEGY',
        pyramid: 'Lean Pyramid 6.0',
        pyramidSub: 'operating model',
        maestro: 'Maestro',
        maestroSub: 'orchestration',
        workers: ['Human', 'AI worker', 'AI worker'],
        flow: 'SOFTWARE FLOW',
        observed: 'OBSERVED PERFORMANCE',
        pillars: 'VISIBILITY · CONTROL · PREDICTABILITY',
        feedback: 'feedback into the operating system',
      },
    },
    screens: {
      kicker: 'The system, on screen',
      title: 'Every screen answers a management question.',
      shots: [
        {
          q: 'What is happening right now?',
          lead: 'Know where everything stands without calling a meeting.',
          src: '/images/maestro/cockpit.jpg',
          alt: 'Maestro cockpit showing deadlines, a late release with recovery forecast, impediments and decisions',
          caption: 'The cockpit: deadlines, the burn-up with its forecast, impediments in the open, decisions waiting on named people. A release running late says so, with best and worst dates.',
        },
        {
          q: 'Are we building the right things?',
          lead: 'See the whole scope, release by release, on one screen.',
          src: '/images/maestro/map.jpg',
          alt: 'Maestro map view: systems and features across the backbone, releases as bands, cards sized XS to XL',
          caption: 'The map lays out the whole Delivery Flow: features across the backbone, releases as bands down the side, cards sized XS to XL. Shipped releases sit above, the current one is highlighted, everything else waits below.',
        },
        {
          q: 'Where is work waiting?',
          lead: 'See where work is stuck, and who it is waiting on.',
          src: '/images/maestro/flow.jpg',
          alt: 'Maestro flow view: work in progress under an explicit limit, a waiting lane, and cards carrying their why',
          caption: 'The flow: work in progress under an explicit limit, a lane for what waits on the client with names and days attached, and every card carrying its why. An item at risk says why, in red, with who flagged it.',
        },
        {
          q: 'What should happen next?',
          lead: 'Know the next most valuable thing to do, and who overruled the queue.',
          src: '/images/maestro/backlog-economics.jpg',
          alt: 'Maestro backlog economics: a value-by-risk chart beside the queue, with a pinned card carrying its reason',
          caption: 'The queue orders itself by value, risk, and how long the work really takes. When a human overrules it, the pin carries their name and their reason.',
        },
        {
          q: 'When can we actually deliver?',
          lead: 'Know when it will really land, before you promise a date.',
          src: '/images/maestro/metrics-project.jpg',
          alt: 'Maestro metrics view with a project burn-up and a Monte Carlo forecast',
          caption: 'The likely date is computed from observed throughput and judged against the delivery date. On track is a computed judgment, and the histogram shows the 10,000 simulated futures behind it.',
        },
        {
          q: 'How is the system performing?',
          lead: 'Know whether delivery is getting faster or slower before customers feel it.',
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Maestro metrics tab: cycle time percentiles, weekly throughput, cumulative flow and the value-ordered queue',
          caption: 'Cycle time by size class, throughput with velocity bands, cumulative flow, and the queue ordered by value over measured duration. Where history is short, the charts say so.',
        },
      ],
    },
    contrast: {
      kicker: 'Beyond tracking',
      title: 'Tracking tools ask people to describe reality. Maestro records it.',
      body: 'In the tools you know, people spend their day describing work to the software: moving cards, updating estimates, preparing reports. In Maestro the board is where the work actually happens, so it is always current: humans and agents move their own work, every move lands in the record, and the reporting takes care of itself. Your people keep their attention on decisions.',
      cards: [
        {
          title: 'Where demand comes from',
          body: 'Agents listen to the work as it happens, meetings, documents, decisions, and turn it into cards on the board. A person reviews and approves.',
        },
        {
          title: 'How agents fit',
          body: 'Agents are workers, never in charge. They follow the same rules people do, and nothing they finish counts until it is proven. Agents get verified. People get trusted.',
        },
      ],
    },
    status: {
      kicker: 'Where Maestro stands',
      body: 'Maestro is new. The operating model behind it is not. It runs our own client engagements in production today, it opens to partners first, and you can adopt it without a consulting engagement: transformation help exists for when you want the organization redesigned around the system. The fastest demo is the board your own work would run on.',
    },
  },

  'pt-BR': {
    seo: {
      title: 'Maestro | O sistema operacional da entrega de software na era da IA',
      description:
        'A IA mudou como o software é construído. O Maestro muda como ele é gerenciado: humanos e agentes de IA entregando em um único sistema, com visibilidade, controle e previsibilidade calculados do próprio trabalho.',
    },
    hero: {
      kicker: 'Maestro',
      headline: 'A entrega de software precisa de um novo sistema operacional.',
      body: 'A IA mudou como o software é construído. Ela tem que mudar como o software é gerenciado. O Maestro reúne o trabalho, as decisões, o fluxo, as métricas, os humanos e os agentes de IA em um único sistema, para você ver o que está acontecendo, controlar o que acontece a seguir, e saber o que consegue entregar de forma realista.',
      primary: 'Veja como o Maestro funciona',
      primaryHref: '#questions',
    },
    heroSvg: {
      aria: 'Humanos e agentes de IA entregam através do Maestro, que executa as políticas e renderiza a verdade da operação.',
      workers: ['Líder de produto', 'Engenheiro', 'Agente de IA', 'Agente de IA'],
      workersLabel: 'OS TRABALHADORES',
      center: 'Maestro',
      centerSub: 'O SISTEMA OPERACIONAL',
      gates: ['Uma fila de entrada', 'Limites de trabalho em andamento', 'Evidência exigida no done'],
      rightLabel: 'A VERDADE, RENDERIZADA',
      chip: 'ON TRACK',
      chipSub: 'computado, com probabilidades',
      doneLabel: '23 prontos',
      fanBest: 'melhor 7 set',
      fanLikely: 'provável 14 set',
      fanWorst: 'pior 21 set',
      axis: 'burn-up · previsão · registro',
    },
    pattern: {
      kicker: 'Um padrão que vemos há décadas',
      title: 'Ocupado não é a mesma coisa que fluindo.',
      body: 'O time trabalha duro. E mesmo assim:',
      symptoms: ['Os clientes não estão felizes', 'As datas vivem escorregando', 'As prioridades mudam o tempo todo', 'A capacidade é um chute', 'Projetos demais ao mesmo tempo', 'A lista de bugs só cresce', 'Reuniões se multiplicam e decidem pouco', 'Os melhores começam a sair'],
      close: 'Parecem problemas separados. Raramente são. São sintomas de um único sistema de entrega que não flui, e nós diagnosticamos esse padrão muito antes de existir IA.',
    },
    problem: {
      kicker: 'Aí a IA subiu a aposta',
      title: 'Os agentes entraram no time. O sistema de gestão não percebeu.',
      body: 'Seus engenheiros já entregam ao lado de agentes de IA. As ferramentas que gerenciam a entrega ainda assumem que cada unidade de trabalho vem de uma pessoa: estimativas, planos de sprint, reuniões de status, quadros que alguém atualiza à mão. É essa lacuna que o Maestro fecha: um sistema de gestão onde humanos e agentes trabalham juntos, e a operação é conduzida como uma só.',
      shifts: [
        {
          name: 'Capacidade se soltou do headcount',
          detail: 'Um engenheiro hoje dirige vários agentes trabalhando ao mesmo tempo. Contar pessoas deixou de prever o que a operação consegue entregar.',
        },
        {
          name: 'Cycle times desabaram',
          detail: 'Trabalho que levava dias às vezes chega em horas. Um relatório semanal de status envelhece antes da reunião para a qual foi escrito.',
        },
        {
          name: 'A parte cara mudou de lugar',
          detail: 'Produzir código ficou barato. Decidir o que construir, em que ordem, e verificar o que volta é onde a entrega se ganha agora.',
        },
      ],
    },
    questions: {
      kicker: 'O problema de gestão',
      title: 'Um líder deveria abrir uma tela e saber a verdade da operação.',
      body: 'Sem perguntar a dez pessoas, montar uma planilha, ou convocar mais uma reunião de status. O Maestro existe para responder seis perguntas continuamente.',
      items: [
        {
          q: 'O que está acontecendo agora?',
          a: 'Trabalho ativo, filas, bloqueios e o que acabou de terminar renderizam ao vivo do registro do trabalho. Ninguém compila relatório de status.',
        },
        {
          q: 'O que deve acontecer a seguir?',
          a: 'A fila se ordena sozinha por valor de negócio, risco, e quanto tempo um trabalho daquele tamanho realmente leva. Quando um humano passa por cima, a decisão carrega nome e razão.',
        },
        {
          q: 'Estamos trabalhando nas coisas certas?',
          a: 'Cada cartão carrega seu porquê e pertence a uma release com data e intenção. Ocupado fica visível, e ocupado na coisa errada também.',
        },
        {
          q: 'Como o sistema está performando?',
          a: 'Throughput, cycle time, trabalho em andamento, envelhecimento e retrabalho, medidos em itens de trabalho e ações de agentes, nunca em pessoas.',
        },
        {
          q: 'Quando conseguimos entregar de verdade?',
          a: 'Previsões calculadas da sua própria história, com probabilidades, no lugar de uma data que alguém digitou num plano três meses atrás.',
        },
        {
          q: 'Onde um humano precisa entrar?',
          a: 'Decisões esperam por pessoas nomeadas, impedimentos dizem por quem esperam, e chamadas irreversíveis são reservadas a humanos por política escrita.',
        },
      ],
    },
    pillars: {
      kicker: 'O que você está comprando',
      title: 'Visibilidade. Controle. Previsibilidade.',
      items: [
        {
          name: 'Visibilidade',
          detail: 'O estado real da operação, extraído do registro de eventos, e não do que as pessoas lembram de reportar.',
        },
        {
          name: 'Controle',
          detail: 'Políticas escritas governam como o trabalho entra e se move: entrada por uma fila única, limites de trabalho em andamento, evidência exigida no done. Você governa o sistema em vez de perseguir cada trabalhador.',
        },
        {
          name: 'Previsibilidade',
          detail: 'Datas de entrega vêm da performance observada, com probabilidades, e o compromisso vira decisão de negócio em vez de ritual de estimativa.',
        },
      ],
      beneath: 'Movidas por orquestração: humanos e agentes de IA operando como um sistema só, com agentes cobrados por evidência e pessoas confiadas pelo julgamento.',
    },
    model: {
      kicker: 'O modelo operacional',
      title: 'Trinta anos de prática de entrega, codificados em software.',
      body: 'O Maestro não partiu de uma lista de features. Partiu da Pirâmide Lean, o modelo operacional refinado ao longo de uma década consertando como organizações de software entregam: a estratégia define a direção, a gestão opera o fluxo, a engenharia entrega valor verificado. O Maestro roda esse modelo dentro do trabalho, e o método chega junto com o sistema em vez de morar num slide.',
      layers: ['Estratégia', 'Gestão', 'Engenharia'],
      layersOutro: 'Humanos e agentes de IA são os trabalhadores. O Maestro orquestra o sistema em que eles trabalham.',
      cta: 'Leia o cânon da Pirâmide Lean',
      ctaHref: '/insights',
      svg: {
        aria: 'A estratégia de negócio desce pela Pirâmide Lean até o Maestro, que orquestra humanos e trabalhadores de IA; a performance observada realimenta o sistema operacional.',
        strategy: 'ESTRATÉGIA DE NEGÓCIO',
        pyramid: 'Pirâmide Lean 6.0',
        pyramidSub: 'modelo operacional',
        maestro: 'Maestro',
        maestroSub: 'orquestração',
        workers: ['Humano', 'Trabalhador IA', 'Trabalhador IA'],
        flow: 'FLUXO DE SOFTWARE',
        observed: 'PERFORMANCE OBSERVADA',
        pillars: 'VISIBILIDADE · CONTROLE · PREVISIBILIDADE',
        feedback: 'feedback para o sistema operacional',
      },
    },
    screens: {
      kicker: 'O sistema, na tela',
      title: 'Cada tela responde uma pergunta de gestão.',
      shots: [
        {
          q: 'O que está acontecendo agora?',
          lead: 'Saiba onde tudo está sem convocar reunião.',
          src: '/images/maestro/cockpit.jpg',
          alt: 'Cockpit do Maestro mostrando deadlines, uma release atrasada com previsão de recuperação, impedimentos e decisões',
          caption: 'O cockpit: deadlines, o burn-up com sua previsão, impedimentos às claras, decisões esperando por pessoas nomeadas. Uma release atrasada diz que está, com datas de melhor e pior caso.',
        },
        {
          q: 'Estamos construindo as coisas certas?',
          lead: 'Veja o escopo inteiro, release por release, em uma tela.',
          src: '/images/maestro/map.jpg',
          alt: 'Visão de mapa do Maestro: sistemas e features no backbone, releases como faixas, cartões de XS a XL',
          caption: 'O mapa abre o engajamento inteiro: features no backbone, releases como faixas na lateral, cartões de XS a XL. Releases entregues ficam acima, a atual em destaque, o resto espera abaixo.',
        },
        {
          q: 'Onde o trabalho está esperando?',
          lead: 'Veja onde o trabalho está travado, e por quem espera.',
          src: '/images/maestro/flow.jpg',
          alt: 'Visão de fluxo do Maestro: trabalho em andamento sob limite explícito, uma coluna de espera, e cartões com seu porquê',
          caption: 'O fluxo: trabalho em andamento sob limite explícito, uma coluna para o que espera pelo cliente com nomes e dias anexados, e cada cartão com seu porquê. Um item em risco diz por quê, em vermelho, com quem sinalizou.',
        },
        {
          q: 'O que deve acontecer a seguir?',
          lead: 'Saiba a próxima coisa mais valiosa a fazer, e quem passou por cima da fila.',
          src: '/images/maestro/backlog-economics.jpg',
          alt: 'Economia do backlog no Maestro: gráfico de valor por risco ao lado da fila, com um cartão fixado carregando sua razão',
          caption: 'A fila se ordena sozinha por valor, risco, e quanto tempo o trabalho realmente leva. Quando um humano passa por cima, o pin carrega o nome e a razão de quem decidiu.',
        },
        {
          q: 'Quando conseguimos entregar de verdade?',
          lead: 'Saiba quando vai chegar de verdade, antes de prometer uma data.',
          src: '/images/maestro/metrics-project.jpg',
          alt: 'Visão de métricas do Maestro com burn-up do projeto e previsão Monte Carlo',
          caption: 'A data provável é calculada sobre o throughput observado e julgada contra a data de entrega. O "on track" é um julgamento computado, e o histograma mostra os 10.000 futuros simulados por trás dele.',
        },
        {
          q: 'Como o sistema está performando?',
          lead: 'Saiba se a entrega está ficando mais rápida ou mais lenta antes de o cliente sentir.',
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Aba de métricas do Maestro: percentis de cycle time, throughput semanal, fluxo cumulativo e a fila ordenada por valor',
          caption: 'Cycle time por classe de tamanho, throughput com faixas de velocidade, fluxo cumulativo, e a fila ordenada por valor sobre duração medida. Onde o histórico é curto, os gráficos dizem isso.',
        },
      ],
    },
    contrast: {
      kicker: 'Além do tracking',
      title: 'Ferramentas de tracking pedem que pessoas descrevam a realidade. O Maestro a registra.',
      body: 'Nas ferramentas que você conhece, as pessoas passam o dia descrevendo o trabalho para o software: movendo cartões, atualizando estimativas, preparando relatórios. No Maestro o board é onde o trabalho de fato acontece, então está sempre em dia: humanos e agentes movem o próprio trabalho, cada movimento cai no registro, e o reporte se resolve sozinho. Seu time fica com a atenção nas decisões.',
      cards: [
        {
          title: 'De onde vem a demanda',
          body: 'Agentes escutam o engajamento, reuniões, documentos, decisões, e transformam isso em trabalho no board. Uma pessoa revisa e aprova.',
        },
        {
          title: 'Onde entram os agentes',
          body: 'Agentes são trabalhadores, nunca o chefe. Seguem as mesmas regras que as pessoas, e nada que terminam conta antes de ser provado. Agentes são verificados. Pessoas recebem confiança.',
        },
      ],
    },
    status: {
      kicker: 'Onde o Maestro está',
      body: 'O Maestro é novo. O modelo operacional por trás dele não é. Ele roda nossos próprios engajamentos com clientes em produção hoje, abre primeiro para parceiros, e você pode adotá-lo sem contratar consultoria: a ajuda de transformação existe para quando você quiser a organização redesenhada em volta do sistema. A demo mais rápida é o board em que o seu próprio trabalho rodaria.',
    },
  },

  es: {
    seo: {
      title: 'Maestro | El sistema operativo de la entrega de software en la era de la IA',
      description:
        'La IA cambió cómo se construye el software. Maestro cambia cómo se gestiona: humanos y agentes de IA entregando en un solo sistema, con visibilidad, control y previsibilidad calculados del propio trabajo.',
    },
    hero: {
      kicker: 'Maestro',
      headline: 'La entrega de software necesita un nuevo sistema operativo.',
      body: 'La IA cambió cómo se construye el software. Tiene que cambiar cómo se gestiona. Maestro reúne el trabajo, las decisiones, el flujo, las métricas, los humanos y los agentes de IA en un solo sistema, para que veas qué está pasando, controles qué pasa después, y sepas qué puedes entregar de forma realista.',
      primary: 'Mira cómo funciona Maestro',
      primaryHref: '#questions',
    },
    heroSvg: {
      aria: 'Humanos y agentes de IA entregan a través de Maestro, que ejecuta las políticas y renderiza la verdad de la operación.',
      workers: ['Líder de producto', 'Ingeniero', 'Agente de IA', 'Agente de IA'],
      workersLabel: 'LOS TRABAJADORES',
      center: 'Maestro',
      centerSub: 'EL SISTEMA OPERATIVO',
      gates: ['Una sola cola de entrada', 'Límites de trabajo en curso', 'Evidencia exigida en done'],
      rightLabel: 'LA VERDAD, RENDERIZADA',
      chip: 'ON TRACK',
      chipSub: 'computado, con probabilidades',
      doneLabel: '23 listos',
      fanBest: 'mejor 7 sep',
      fanLikely: 'probable 14 sep',
      fanWorst: 'peor 21 sep',
      axis: 'burn-up · pronóstico · registro',
    },
    pattern: {
      kicker: 'Un patrón que vemos hace décadas',
      title: 'Ocupado no es lo mismo que fluyendo.',
      body: 'El equipo trabaja duro. Y aun así:',
      symptoms: ['Los clientes no están contentos', 'Las fechas se siguen corriendo', 'Las prioridades cambian todo el tiempo', 'La capacidad es una adivinanza', 'Demasiados proyectos a la vez', 'La lista de bugs no deja de crecer', 'Las reuniones se multiplican y deciden poco', 'Los mejores empiezan a irse'],
      close: 'Parecen problemas separados. Rara vez lo son. Son síntomas de un solo sistema de entrega que no fluye, y nosotros diagnosticábamos ese patrón mucho antes de que existiera la IA.',
    },
    problem: {
      kicker: 'Entonces la IA subió la apuesta',
      title: 'Los agentes entraron al equipo. El sistema de gestión no se dio cuenta.',
      body: 'Tus ingenieros ya entregan junto a agentes de IA. Las herramientas que gestionan la entrega todavía asumen que cada unidad de trabajo viene de una persona: estimaciones, planes de sprint, reuniones de estado, tableros que alguien actualiza a mano. Esa brecha es la que Maestro cierra: un sistema de gestión donde humanos y agentes trabajan juntos, y la operación se conduce como una sola.',
      shifts: [
        {
          name: 'La capacidad se soltó del headcount',
          detail: 'Un ingeniero hoy dirige varios agentes trabajando a la vez. Contar personas dejó de predecir lo que la operación puede entregar.',
        },
        {
          name: 'Los cycle times se desplomaron',
          detail: 'Trabajo que tomaba días a veces llega en horas. Un reporte semanal de estado envejece antes de la reunión para la que fue escrito.',
        },
        {
          name: 'La parte cara cambió de lugar',
          detail: 'Producir código se volvió barato. Decidir qué construir, en qué orden, y verificar lo que vuelve es donde se gana la entrega ahora.',
        },
      ],
    },
    questions: {
      kicker: 'El problema de gestión',
      title: 'Un líder debería abrir una pantalla y saber la verdad de la operación.',
      body: 'Sin preguntarle a diez personas, armar una planilla, o convocar otra reunión de estado. Maestro existe para responder seis preguntas continuamente.',
      items: [
        {
          q: '¿Qué está pasando ahora?',
          a: 'Trabajo activo, colas, bloqueos y lo que acaba de terminar se renderizan en vivo desde el registro del trabajo. Nadie compila un reporte de estado.',
        },
        {
          q: '¿Qué debe pasar después?',
          a: 'La cola se ordena sola por valor de negocio, riesgo, y cuánto tarda de verdad un trabajo de ese tamaño. Cuando un humano la anula, la decisión lleva nombre y razón.',
        },
        {
          q: '¿Estamos trabajando en lo correcto?',
          a: 'Cada tarjeta lleva su porqué y pertenece a una release con fecha e intención. Lo ocupado se ve, y lo ocupado en lo equivocado también.',
        },
        {
          q: '¿Cómo está rindiendo el sistema?',
          a: 'Throughput, cycle time, trabajo en curso, envejecimiento y retrabajo, medidos en ítems de trabajo y acciones de agentes, nunca en personas.',
        },
        {
          q: '¿Cuándo podemos entregar de verdad?',
          a: 'Pronósticos calculados de tu propia historia, con probabilidades, en lugar de una fecha que alguien tipeó en un plan hace tres meses.',
        },
        {
          q: '¿Dónde necesita entrar un humano?',
          a: 'Las decisiones esperan a personas con nombre, los impedimentos dicen a quién esperan, y las llamadas irreversibles se reservan a humanos por política escrita.',
        },
      ],
    },
    pillars: {
      kicker: 'Qué estás comprando',
      title: 'Visibilidad. Control. Previsibilidad.',
      items: [
        {
          name: 'Visibilidad',
          detail: 'El estado real de la operación, extraído del registro de eventos y no de lo que la gente recuerda reportar.',
        },
        {
          name: 'Control',
          detail: 'Políticas escritas gobiernan cómo el trabajo entra y se mueve: entrada por una sola cola, límites de trabajo en curso, evidencia exigida en done. Gobiernas el sistema en vez de perseguir a cada trabajador.',
        },
        {
          name: 'Previsibilidad',
          detail: 'Las fechas de entrega salen del desempeño observado, con probabilidades, y el compromiso se vuelve decisión de negocio en vez de ritual de estimación.',
        },
      ],
      beneath: 'Impulsadas por la orquestación: humanos y agentes de IA operando como un solo sistema, con agentes exigidos por evidencia y personas confiadas por su juicio.',
    },
    model: {
      kicker: 'El modelo operativo',
      title: 'Treinta años de práctica de entrega, codificados en software.',
      body: 'Maestro no partió de una lista de features. Partió de la Pirámide Lean, el modelo operativo refinado durante una década arreglando cómo entregan las organizaciones de software: la estrategia marca la dirección, la gestión opera el flujo, la ingeniería entrega valor verificado. Maestro corre ese modelo dentro del trabajo, y el método llega con el sistema en vez de vivir en una diapositiva.',
      layers: ['Estrategia', 'Gestión', 'Ingeniería'],
      layersOutro: 'Humanos y agentes de IA son los trabajadores. Maestro orquesta el sistema en el que trabajan.',
      cta: 'Lee el canon de la Pirámide Lean',
      ctaHref: '/insights',
      svg: {
        aria: 'La estrategia de negocio desciende por la Pirámide Lean hasta Maestro, que orquesta humanos y trabajadores de IA; el desempeño observado realimenta el sistema operativo.',
        strategy: 'ESTRATEGIA DE NEGOCIO',
        pyramid: 'Pirámide Lean 6.0',
        pyramidSub: 'modelo operativo',
        maestro: 'Maestro',
        maestroSub: 'orquestación',
        workers: ['Humano', 'Trabajador IA', 'Trabajador IA'],
        flow: 'FLUJO DE SOFTWARE',
        observed: 'DESEMPEÑO OBSERVADO',
        pillars: 'VISIBILIDAD · CONTROL · PREVISIBILIDAD',
        feedback: 'feedback al sistema operativo',
      },
    },
    screens: {
      kicker: 'El sistema, en pantalla',
      title: 'Cada pantalla responde una pregunta de gestión.',
      shots: [
        {
          q: '¿Qué está pasando ahora?',
          lead: 'Sabe dónde está todo sin convocar una reunión.',
          src: '/images/maestro/cockpit.jpg',
          alt: 'Cockpit de Maestro mostrando deadlines, una release tarde con pronóstico de recuperación, impedimentos y decisiones',
          caption: 'El cockpit: deadlines, el burn-up con su pronóstico, impedimentos a la vista, decisiones esperando a personas con nombre. Una release tarde lo dice, con fechas de mejor y peor caso.',
        },
        {
          q: '¿Estamos construyendo lo correcto?',
          lead: 'Ve todo el alcance, release por release, en una pantalla.',
          src: '/images/maestro/map.jpg',
          alt: 'Vista de mapa de Maestro: sistemas y features en el backbone, releases como bandas, tarjetas de XS a XL',
          caption: 'El mapa despliega todo el compromiso: features en el backbone, releases como bandas al costado, tarjetas de XS a XL. Las releases entregadas quedan arriba, la actual resaltada, el resto espera abajo.',
        },
        {
          q: '¿Dónde está esperando el trabajo?',
          lead: 'Ve dónde está trabado el trabajo, y a quién espera.',
          src: '/images/maestro/flow.jpg',
          alt: 'Vista de flujo de Maestro: trabajo en curso bajo límite explícito, una columna de espera, y tarjetas con su porqué',
          caption: 'El flujo: trabajo en curso bajo límite explícito, una columna para lo que espera al cliente con nombres y días adjuntos, y cada tarjeta con su porqué. Un ítem en riesgo dice por qué, en rojo, con quién lo marcó.',
        },
        {
          q: '¿Qué debe pasar después?',
          lead: 'Sabe qué es lo próximo más valioso, y quién anuló la cola.',
          src: '/images/maestro/backlog-economics.jpg',
          alt: 'Economía del backlog en Maestro: gráfico de valor por riesgo junto a la cola, con una tarjeta fijada que lleva su razón',
          caption: 'La cola se ordena sola por valor, riesgo, y cuánto tarda de verdad el trabajo. Cuando un humano la anula, el pin lleva su nombre y su razón.',
        },
        {
          q: '¿Cuándo podemos entregar de verdad?',
          lead: 'Sabe cuándo va a llegar de verdad, antes de prometer una fecha.',
          src: '/images/maestro/metrics-project.jpg',
          alt: 'Vista de métricas de Maestro con burn-up del proyecto y pronóstico Monte Carlo',
          caption: 'La fecha probable se calcula sobre el throughput observado y se juzga contra la fecha de entrega. El "on track" es un juicio computado, y el histograma muestra los 10.000 futuros simulados detrás.',
        },
        {
          q: '¿Cómo está rindiendo el sistema?',
          lead: 'Sabe si la entrega se está acelerando o frenando antes de que el cliente lo sienta.',
          src: '/images/maestro/metrics-flow.jpg',
          alt: 'Pestaña de métricas de Maestro: percentiles de cycle time, throughput semanal, flujo acumulado y la cola ordenada por valor',
          caption: 'Cycle time por clase de tamaño, throughput con bandas de velocidad, flujo acumulado, y la cola ordenada por valor sobre duración medida. Donde la historia es corta, los gráficos lo dicen.',
        },
      ],
    },
    contrast: {
      kicker: 'Más allá del tracking',
      title: 'Las herramientas de tracking piden que la gente describa la realidad. Maestro la registra.',
      body: 'En las herramientas que conoces, la gente pasa el día describiéndole el trabajo al software: moviendo tarjetas, actualizando estimaciones, preparando reportes. En Maestro el tablero es donde el trabajo realmente sucede, así que siempre está al día: humanos y agentes mueven su propio trabajo, cada movimiento cae en el registro, y el reporte se resuelve solo. Tu equipo queda con la atención en las decisiones.',
      cards: [
        {
          title: 'De dónde viene la demanda',
          body: 'Los agentes escuchan el compromiso, reuniones, documentos, decisiones, y lo convierten en trabajo en el tablero. Una persona revisa y aprueba.',
        },
        {
          title: 'Dónde entran los agentes',
          body: 'Los agentes son trabajadores, nunca el jefe. Siguen las mismas reglas que las personas, y nada que terminan cuenta antes de ser probado. Los agentes se verifican. Las personas reciben confianza.',
        },
      ],
    },
    status: {
      kicker: 'Dónde está Maestro',
      body: 'Maestro es nuevo. El modelo operativo detrás de él no lo es. Corre nuestros propios compromisos con clientes en producción hoy, se abre primero a socios, y puedes adoptarlo sin contratar consultoría: la ayuda de transformación existe para cuando quieras rediseñar la organización alrededor del sistema. La demo más rápida es el tablero en el que correría tu propio trabajo.',
    },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getMaestroContent = (language) => maestroContent[normalize(language)]
export default maestroContent
